import React, { lazy, Suspense, useEffect } from 'react';

import { Redirect, Route, Switch } from 'react-router-dom';
import PrivateRoute from 'base/PrivateRoute/PrivateRoute';
import { Loading } from 'v1/components/shared';
import Authenticating from 'v1/containers/Routes/Auth/Authenticating/Authenticating';
import RecoverPassword from 'v1/containers/Routes/Auth/RecoverPassword';
import ErrorContainer from 'v1/containers/Errors/ErrorContainer/ErrorContainer';
import NotFoundScreen from 'v5/features/core/not-found/NotFoundScreen.connected';
import Farewell from 'v1/containers/Routes/Auth/Farewell/Farewell';
import ResourcePortal from 'features/ResourcePortal/ResourcePortalRoute';
import UserOnboardingRoute from 'features/UserOnboarding/UserOnboarding';
import { Capability, FeatureFlag } from '__types__';

import Login from 'v1/containers/Routes/Auth/Login/Login';
import AuthService from 'lib/auth/AuthService';
import { loadAuth } from 'store/v1/auth/auth.actions.js';
import { getTeamAccounts, selectAccountsAreFetched } from 'store/accounts';
import { getAccountTeams, selectTeamsAreFetched } from 'store/teams';
import {
  getTeamUserGroups,
  selectUserGroupsAreFetched
} from 'store/userGroups';
import { useSelector } from 'react-redux';
import DocuSignCallback from 'v1/containers/Routes/DocuSignCallback/DocuSignCallback';
import { getIntegrations } from 'store/integrations';
import { withinSettings } from 'v4/core/appRoutes';
import { PublicLocaleProvider } from 'v5/i18n';
import { PublicShortlistAppScope } from 'v4/pages/shortlists/publicDetail/PublicShortlistAppScope';
import { resolveDualPathAppScope } from 'v5/core/app-scope/resolveDualPathAppScope';
import { appRouting } from 'v5/core/routing/appRouting';
import { myRouting } from 'v5/features/core/routing';
import { withRootServices } from 'v5/services/services.connected.app';

/** Placeholder paths for enterprise dual-route registration in this file. */
const app = appRouting.org();
const appWorkspace = () => app.workspace();
const appTeam = () => appWorkspace().team();

const Onboard = lazy(() => import('v1/containers/Routes/Auth/Onboard/Onboard'));
// prettier-ignore
const Account = lazy(() => import('v1/containers/Routes/Account/Account'));
const Contact = lazy(() => import('v1/containers/Routes/Contact/Contact'));
const ShortlistDetailPage = lazy(
  () => import('~/v4/pages/shortlists/detail/ShortlistDetailPage.connected')
);
const ShortlistsPageConnected = lazy(
  () => import('~/v5/features/shortlists/ShortlistsPage.connected')
);
const CalloutsPageConnected = lazy(
  () => import('~/v5/features/callouts/CalloutsPage.connected')
);
const BookingsPageConnected = lazy(
  () => import('~/v5/features/bookings/BookingsPage.connected')
);
const ShortlistPublicDetailPage = lazy(
  () =>
    import(
      'v4/pages/shortlists/publicDetail/PublicShortlistDetailPage.connected'
    )
);
const Callout = lazy(() => import('v1/containers/Routes/Callout/Callout'));
const Callsheet = lazy(
  () => import('v1/containers/Routes/Callsheet/Callsheet')
);
// prettier-ignore
const CallsheetPublic = lazy(() => import('v1/containers/Routes/CallsheetPublic/CallsheetPublic'));
// prettier-ignore
const CalloutPublic = lazy(() => import('v1/containers/Routes/CalloutPublic/CalloutPublic'));

const ArrivalBoard = lazy(
  () => import('v1/containers/Routes/ArrivalBoard/ArrivalBoard')
);

const DashboardScreenConnected = lazy(
  () => import('v5/features/dashboard/DashboardScreen.connected.tsx')
);
// prettier-ignore
const ProductionCalendar = lazy(() => import('v1/containers/Routes/Calendars/ProductionCalendar/ProductionCalendar'));
// prettier-ignore
const ResourceCalendar = lazy(() => import('v1/containers/Routes/Calendars/ResourceCalendar/ResourceCalendar'));
const Production = lazy(
  () => import('v1/containers/Routes/Production/Production')
);
const ProductionsPageConnected = lazy(
  () => import('v5/features/productions/ProductionsPage.connected.tsx')
);
const Reporting = lazy(
  () => import('v1/containers/Routes/Reporting/Reporting')
);
const FrontDesk = lazy(
  () => import('v1/containers/Routes/FrontDesk/FrontDesk')
);

const CallsheetsPageConnected = lazy(
  () => import('v5/features/callsheets/CallsheetsPage.connected.tsx')
);

const ResourcesPageConnected = lazy(
  () => import('v5/features/resources/ResourcesPage.connected.tsx')
);

const OrganizationTalentsPageConnected = lazy(
  () => import('v5/features/resources/OrganizationTalentsPage.connected.tsx')
);

const ReconciliationsPageConnected = lazy(
  () => import('v5/features/reconciliations/ReconciliationsPage.connected.tsx')
);

const ReconciliationsRoute = withRootServices(ReconciliationsPageConnected);

const CallsheetsRoute = withRootServices(CallsheetsPageConnected);

const ResourcesRoute = withRootServices(ResourcesPageConnected);

const OrganizationTalentsRoute = withRootServices(
  OrganizationTalentsPageConnected
);

const ShortlistsRoute = withRootServices(ShortlistsPageConnected);

const CalloutsRoute = withRootServices(CalloutsPageConnected);

const BookingsRoute = withRootServices(BookingsPageConnected);

const ProductionsRoute = withRootServices(ProductionsPageConnected);

// eslint-disable-next-line import/no-anonymous-default-export
export default ({ store }) => {
  const teamsAreFetched = useSelector(selectTeamsAreFetched);
  const accountsAreFetched = useSelector(selectAccountsAreFetched);
  const userGroupsAreFetched = useSelector(selectUserGroupsAreFetched);
  const settingsRoute = withinSettings();

  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      const token = AuthService.token();
      store.dispatch(loadAuth(token, token.tokenString, ''));
      store.dispatch(getAccountTeams());
      store.dispatch(getTeamAccounts());
      store.dispatch(getTeamUserGroups());
      store.dispatch(getIntegrations());
    }
  }, [store]);

  if (
    AuthService.isAuthenticated() &&
    (!teamsAreFetched || !accountsAreFetched || !userGroupsAreFetched)
  ) {
    return <Loading className="pageCenter" />;
  }

  return (
    <Switch>
      <Route exact path="/login" component={Login} />
      <Route exact path="/authenticating" component={Authenticating} />
      <Route exact path="/recover_password" component={RecoverPassword} />
      <Route exact path="/farewell" component={Farewell} />
      <Route
        exact
        path="/unauthorized"
        render={() => <ErrorContainer error={{ status: 403 }} />}
      />
      <Route
        exact
        path="/logindenied"
        render={() => <ErrorContainer error={{ status: 500 }} />}
      />
      <Route exact path="/" render={() => <Redirect to="/app" />} />
      <PrivateRoute
        exact
        path="/docusign_callback"
        component={DocuSignCallback}
      />
      <PrivateRoute exact path="/app" component={null} />
      <PrivateRoute
        path={settingsRoute.settings.home()}
        resolveAppScope={resolveDualPathAppScope}
        component={Account}
      />
      <PrivateRoute exact path="/app/:app_id" component={null} />
      <PrivateRoute
        exact
        path={['/app/:app_id/dashboard', appWorkspace().dashboard()]}
        resolveAppScope={resolveDualPathAppScope}
        render={() => <DashboardScreenConnected />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/resources', appWorkspace().resources.root()]}
        flag={FeatureFlag.RESOURCES}
        capabilities={Capability.RESOURCE_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <ResourcesRoute {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={[
          '/app/:app_id/resources/:id',
          appWorkspace().resources.forResourceType()
        ]}
        flag={FeatureFlag.RESOURCES}
        capabilities={Capability.RESOURCE_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => {
          const rawTypeId =
            routeProps.match.params.resourceTypeId ??
            routeProps.match.params.id;
          const resourceTypeId = Number(rawTypeId);
          return (
            <ResourcesRoute
              {...routeProps}
              resourceTypeId={
                Number.isNaN(resourceTypeId) ? undefined : resourceTypeId
              }
            />
          );
        }}
      />
      <PrivateRoute
        exact
        path={app.organization.organizationTalents()}
        flag={FeatureFlag.RESOURCES}
        capabilities={Capability.RESOURCE_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <OrganizationTalentsRoute {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/contacts/:id', appWorkspace().contacts.detail()]}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Contact {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/contacts/:id/:tab', appWorkspace().contacts.tab()]}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Contact {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/shortlists', appTeam().shortlists.root()]}
        flag={FeatureFlag.SHORTLISTS}
        capabilities={Capability.SHORTLIST_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <ShortlistsRoute {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/shortlists/:id', appTeam().shortlists.detail()]}
        flag={FeatureFlag.SHORTLISTS}
        capabilities={Capability.SHORTLIST_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <ShortlistDetailPage {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/callouts', appWorkspace().callouts.root()]}
        flag={FeatureFlag.CALLOUTS}
        capabilities={Capability.CALLOUT_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <CalloutsRoute {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/callouts/:id', appWorkspace().callouts.detail()]}
        flag={FeatureFlag.CALLOUTS}
        capabilities={Capability.CALLOUT_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Callout {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={[
          myRouting.forWorkspace().bookings().root(),
          appWorkspace().bookings.root()
        ]}
        flag={FeatureFlag.BOOKINGS}
        capabilities={Capability.BOOKING_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <BookingsRoute {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={[
          myRouting.forWorkspace().bookings().type(),
          appWorkspace().bookings.type()
        ]}
        flag={FeatureFlag.BOOKINGS}
        capabilities={Capability.BOOKING_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => {
          const bookingTypeId = Number(routeProps.match.params.bookingTypeId);
          return (
            <BookingsRoute
              {...routeProps}
              bookingTypeId={
                Number.isNaN(bookingTypeId) ? undefined : bookingTypeId
              }
            />
          );
        }}
      />
      {/* TODO: Create Booking detail component */}
      {/* <PrivateRoute
        exact
        path="/app/:app_id/bookings/:id"
        flag={FeatureFlag.BOOKINGS}
        component={Booking}
        capabilities={Capability.BOOKING_READ}
      /> */}
      <PrivateRoute
        exact
        path={[
          myRouting.forWorkspace().productions().root(),
          appTeam().productions.root()
        ]}
        flag={FeatureFlag.PRODUCTIONS}
        capabilities={Capability.PRODUCTION_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => (
          <ProductionsRoute workspace={routeProps.match.params.app_id ?? ''} />
        )}
      />
      <PrivateRoute
        exact
        path={[
          myRouting.forWorkspace().productions().type(),
          appTeam().productions.type()
        ]}
        flag={FeatureFlag.PRODUCTIONS}
        capabilities={Capability.PRODUCTION_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => (
          <ProductionsRoute
            workspace={routeProps.match.params.app_id ?? ''}
            productionTypeId={Number(routeProps.match.params.productionTypeId)}
          />
        )}
      />
      <PrivateRoute
        exact
        path={[
          myRouting.forWorkspace().productions().detail(),
          appTeam().productions.detail()
        ]}
        flag={FeatureFlag.PRODUCTIONS}
        capabilities={Capability.PRODUCTION_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Production {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={[
          myRouting.forWorkspace().productions().resourcing().tab(),
          appTeam().productions.resourcing().tab()
        ]}
        flag={FeatureFlag.PRODUCTIONS}
        capabilities={Capability.PRODUCTION_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Production {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={[
          `${myRouting.forWorkspace().productions().detail()}/:section`,
          `${appTeam().productions.detail()}/:section`
        ]}
        flag={FeatureFlag.PRODUCTIONS}
        capabilities={Capability.PRODUCTION_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Production {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={[
          `${myRouting.forWorkspace().productions().detail()}/:section/:teamMember`,
          `${appTeam().productions.detail()}/:section/:teamMember`
        ]}
        flag={FeatureFlag.PRODUCTIONS}
        capabilities={Capability.RESOURCE_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Production {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/callsheets', appTeam().callsheets.root()]}
        flag={FeatureFlag.CALLSHEETS}
        capabilities={Capability.CALLSHEET_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <CallsheetsRoute {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/callsheets/:id', appTeam().callsheets.detail()]}
        flag={FeatureFlag.CALLSHEETS}
        capabilities={Capability.CALLSHEET_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={routeProps => <Callsheet {...routeProps} />}
      />
      <PrivateRoute
        exact
        path={[
          '/app/:app_id/calendar/productions',
          appWorkspace().calendar.productions.home()
        ]}
        flag={FeatureFlag.PRODUCTIONS}
        capabilities={Capability.PRODUCTION_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={() => <ProductionCalendar />}
      />
      <PrivateRoute
        exact
        path={[
          '/app/:app_id/calendar/resources',
          appWorkspace().calendar.resources.home()
        ]}
        flag={FeatureFlag.RESOURCES}
        capabilities={Capability.RESOURCE_READ}
        resolveAppScope={resolveDualPathAppScope}
        render={() => <ResourceCalendar />}
      />
      <PrivateRoute
        exact
        path="/app/:app_id/calendar"
        flag={FeatureFlag.CALENDAR}
        component={ResourceCalendar}
        capabilitiesAtLeastOne={[
          Capability.PRODUCTION_READ,
          Capability.RESOURCE_READ
        ]}
      />
      <PrivateRoute
        exact
        path={['/app/:app_id/frontdesk', appTeam().frontdesk.root()]}
        flag={FeatureFlag.NIKECUSTOM_FRONT_DESK}
        capabilities={Capability.CONFIGURE_CUSTOM_NIKE_FRONT_DESK}
        resolveAppScope={resolveDualPathAppScope}
        render={() => <FrontDesk />}
      />
      <PrivateRoute
        exact
        path="/app/:app_id/reconciliations"
        flag={FeatureFlag.RECONCILIATIONS}
        component={ReconciliationsRoute}
        capabilities={Capability.MANAGE_RECONCILIATION}
      />
      <PrivateRoute
        exact
        path="/app/:app_id/reporting"
        flag={FeatureFlag.REPORTING}
        component={Reporting}
        capabilities={Capability.REPORT_DOWNLOAD}
      />
      <Suspense fallback={<Loading />}>
        <Switch>
          <Route
            exact
            path="/app/:app_id/onboard"
            component={Onboard}
            fullpage
          />
          <Route
            exact
            path={[
              myRouting.forWorkspace().callouts().publicCallout(),
              appWorkspace().callouts.publicCallout(':id')
            ]}
            render={props => (
              <PublicLocaleProvider>
                <CalloutPublic {...props} />
              </PublicLocaleProvider>
            )}
          />
          <Route
            exact
            path="/app/:app_id/p/callsheets/:id"
            render={props => (
              <PublicLocaleProvider>
                <CallsheetPublic {...props} />
              </PublicLocaleProvider>
            )}
          />
          <Route
            exact
            path="/app/:app_id/p/shortlists/:id"
            render={props => (
              <PublicLocaleProvider>
                <PublicShortlistAppScope>
                  <ShortlistPublicDetailPage {...props} />
                </PublicShortlistAppScope>
              </PublicLocaleProvider>
            )}
          />
          <PrivateRoute
            exact
            bypassSidebar
            path="/app/:app_id/arrivals"
            flag={FeatureFlag.NIKECUSTOM_ARRIVAL_BOARD}
            render={props => (
              <PublicLocaleProvider>
                <ArrivalBoard orientation="portrait" />
              </PublicLocaleProvider>
            )}
            capabilities={Capability.CONFIGURE_CUSTOM_NIKE_ARRIVAL_BOARD}
          />
          <PrivateRoute
            exact
            bypassSidebar
            path="/app/:app_id/arrivals/landscape"
            flag={FeatureFlag.NIKECUSTOM_ARRIVAL_BOARD}
            render={props => (
              <PublicLocaleProvider>
                <ArrivalBoard orientation="landscape" />
              </PublicLocaleProvider>
            )}
            capabilities={Capability.CONFIGURE_CUSTOM_NIKE_ARRIVAL_BOARD}
          />
          <Route
            path="/app/:team_schema/resource-portal" //app_id and team_schema (api v2) refer to the same thing.
            render={props => (
              <PublicLocaleProvider>
                <ResourcePortal {...props} />
              </PublicLocaleProvider>
            )}
          />
          <Route
            path="/app/:team_schema/onboarding"
            render={props => <UserOnboardingRoute />}
          />
          <Route render={() => <NotFoundScreen />} />
        </Switch>
      </Suspense>
      <Route render={() => <NotFoundScreen />} />
    </Switch>
  );
};
